import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "equal-cookie-consent";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (!saved) {
      // small delay so it doesn't flash during initial paint
      const id = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(id);
    }
  }, []);

  const dismiss = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 animate-fade-up"
    >
      <div className="relative mx-auto max-w-4xl rounded-2xl border border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl p-5 sm:p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]">
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-indigo-500/15 via-transparent to-violet-500/15 -z-10 blur-xl" />

        <div className="flex items-start gap-4">
          <div className="hidden sm:flex h-10 w-10 flex-shrink-0 rounded-lg bg-indigo-500/15 border border-indigo-500/30 items-center justify-center text-indigo-300">
            <Cookie className="h-5 w-5" strokeWidth={1.5} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-500">
              Cookies
            </div>
            <h3 className="mt-1.5 font-display text-white text-lg tracking-tight">
              We use a few cookies to keep Equal working.
            </h3>
            <p className="mt-2 font-body text-[14px] text-zinc-400 leading-relaxed">
              Essential cookies power the site. Optional analytics help us understand how people use
              EqualLayer so we can improve accessibility. You can change your choice anytime — read our{" "}
              <Link
                to="/privacy"
                className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
                data-testid="cookie-privacy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <button
                type="button"
                onClick={() => dismiss("accepted")}
                data-testid="cookie-accept"
                className="inline-flex items-center justify-center rounded-full bg-indigo-500 hover:bg-indigo-400 px-5 py-2.5 text-sm font-medium text-white transition-all shadow-[0_0_24px_rgba(99,102,241,0.35)] hover:shadow-[0_0_36px_rgba(99,102,241,0.55)]"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={() => dismiss("essential")}
                data-testid="cookie-essential"
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20 px-5 py-2.5 text-sm font-medium text-white transition-all"
              >
                Essential only
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => dismiss("essential")}
            aria-label="Dismiss cookie banner"
            data-testid="cookie-dismiss"
            className="flex-shrink-0 h-8 w-8 -mr-1 -mt-1 rounded-md border border-transparent hover:border-white/10 hover:bg-white/[0.04] flex items-center justify-center text-zinc-400 hover:text-white transition-all"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
